import React from 'react';
import { Link } from 'react-router-dom';
import { Award, Target, Users as UsersIcon, TrendingUp, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';

const stats = [
  { number: '200+', label: 'PROJECTS DELIVERED' },
  { number: '50+', label: 'HAPPY CLIENTS' },
  { number: '10', label: 'YEARS EXPERIENCE' },
  { number: '2M+', label: 'TOTAL VIEWS' }
];

const values = [
  {
    icon: Award,
    title: 'EXCELLENCE',
    description: 'We never compromise on quality. Every project receives our full dedication and expertise.'
  },
  {
    icon: Target,
    title: 'STRATEGY',
    description: 'Beautiful visuals mean nothing without purpose. We align creative with business goals.'
  },
  {
    icon: UsersIcon,
    title: 'PARTNERSHIP',
    description: "Your success is our success. We're invested in creating work that delivers real results."
  },
  {
    icon: TrendingUp,
    title: 'INNOVATION',
    description: 'We stay ahead of trends and technology to give your brand a competitive edge.'
  }
];

const AboutAlt = () => {
  return (
    <div className="bg-white min-h-screen pt-20">
      {/* Hero */}
      <section className="py-20 px-6 lg:px-12">
        <div className="max-w-screen-2xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-8xl lg:text-[10vw] font-black uppercase leading-none mb-8">
                WE CRAFT
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-red-600">
                  STORIES
                </span>
              </h1>
              <p className="text-2xl font-semibold leading-relaxed">
                A commercial filmmaking studio specializing in brand films, podcasts, and client stories for ambitious businesses.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1492619375914-88005aa9e8fb?w=800&q=80"
                alt="Film production"
                className="w-full aspect-[4/3] object-cover"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 px-6 lg:px-12 bg-black text-white">
        <div className="max-w-screen-2xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-6xl lg:text-7xl font-black mb-2">{stat.number}</div>
                <div className="text-sm font-bold uppercase tracking-wider text-gray-500">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-6xl lg:text-7xl font-black uppercase mb-12"
          >
            OUR STORY
          </motion.h2>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="space-y-6 text-xl leading-relaxed text-gray-700"
          >
            <p>
              With over a decade of experience in commercial production, we've helped healthcare providers, real estate professionals, and content creators tell their stories in ways that captivate audiences and drive measurable results.
            </p>
            <p>
              Our approach combines cinematic artistry with strategic thinking. Every frame serves a purpose. Every edit moves the narrative forward. We don't just create beautiful videos – we craft visual experiences that convert viewers into clients.
            </p>
            <p>
              From intimate patient testimonials to sweeping property showcases, we bring the same level of craftsmanship and attention to detail that defines the world's most iconic brands.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 px-6 lg:px-12 bg-gray-50">
        <div className="max-w-screen-2xl mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-7xl lg:text-8xl font-black uppercase leading-none mb-20"
          >
            WHAT DRIVES US
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const IconComponent = value.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white p-8"
                >
                  <div className="w-16 h-16 bg-black flex items-center justify-center mb-6">
                    <IconComponent className="text-white" size={32} />
                  </div>
                  <h3 className="text-2xl font-black uppercase mb-4">{value.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{value.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Team Images */}
      <section className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-screen-2xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="aspect-[4/3] overflow-hidden"
            >
              <img
                src="https://images.unsplash.com/photo-1556761175-b413da4baf72?w=800&q=80"
                alt="Film crew on set"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="aspect-[4/3] overflow-hidden"
            >
              <img
                src="https://images.unsplash.com/photo-1574717024653-61fd2cf4d44d?w=800&q=80"
                alt="Behind the scenes"
                className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 lg:px-12 bg-black text-white">
        <div className="max-w-screen-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-6xl lg:text-8xl font-black uppercase leading-none mb-12">
              LET'S WORK
              <br />
              TOGETHER
            </h2>
            <Link
              to="/contact"
              className="inline-flex items-center bg-white text-black px-12 py-6 text-xl font-black uppercase hover:bg-gray-200 transition-colors group"
            >
              GET IN TOUCH
              <ArrowRight className="ml-3 group-hover:translate-x-2 transition-transform" size={24} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutAlt;